// Las preguntas del onboarding de marca: lo que no se puede deducir del sitio
// (ADR-2026-09-28-onboarding-v2-etapa-1). Son obligatorias y cada una tiene "Otro"
// (ADR-2026-09-29-onboarding-v2-preguntas-con-otro). Desde
// ADR-2026-10-02-onboarding-v2-preguntas-con-cumbre son un paso propio, entre el link y el
// tablero, y las hace Cumbre: acá está también lo que dice en cada una. Todo puro.

import { SETUP_ROLES } from '../../components/onboarding/accountSetup';
import type { Reaccion, Tramo } from './dialogo';
import type { RespuestasDeLaEspera } from './marca';

export type ClaveDePregunta = 'primero' | 'rol' | 'anunciosPagos' | 'canal';

export interface OpcionDePregunta {
  id: string;
  label: string;
  /** Glifo de Material Symbols Rounded. */
  icono: string;
  /** Excluye a las demás ("Todavía no", "Ninguno"): marcarla desmarca el resto, y al revés. */
  exclusiva?: boolean;
}

export interface Pregunta {
  clave: ClaveDePregunta;
  titulo: string;
  opciones: OpcionDePregunta[];
  ejemploOtro: string;
  /** Por qué se pregunta, dicho por Cumbre debajo de la pregunta. */
  porQue: string;
  /** El tema de la pregunta en el indicador de progreso (Material Symbols Rounded). */
  icono: string;
}

const ICONO_DE_ROL: Record<string, string> = {
  community_manager: 'forum',
  owner: 'storefront',
  employee: 'badge',
  agency: 'groups',
};

export const PREGUNTAS: Pregunta[] = [
  {
    clave: 'primero',
    titulo: '¿Qué te gustaría crear primero?',
    opciones: [
      { id: 'anuncios', label: 'Anuncios para redes', icono: 'campaign' },
      { id: 'foto_producto', label: 'Fotos de catálogo', icono: 'shopping_bag' },
      { id: 'fotos_redes', label: 'Contenido para redes', icono: 'photo_camera' },
      { id: 'videos', label: 'Videos', icono: 'movie' },
    ],
    ejemploOtro: 'Ej: banners para la web',
    porQue: 'Con eso armo tus primeras piezas.',
    icono: 'auto_awesome',
  },
  {
    clave: 'rol',
    titulo: '¿Cuál es tu rol en la marca?',
    opciones: SETUP_ROLES.map((r) => ({ id: r.id, label: r.label, icono: ICONO_DE_ROL[r.id] ?? 'person' })),
    ejemploOtro: 'Ej: diseñadora',
    porQue: 'Así sé cómo hablarte.',
    icono: 'person',
  },
  {
    clave: 'anunciosPagos',
    titulo: '¿Tu marca ya invierte en anuncios?',
    opciones: [
      { id: 'no', label: 'Todavía no', icono: 'schedule', exclusiva: true },
      { id: 'meta', label: 'Sí, en Meta', icono: 'ads_click' },
      { id: 'meta_google', label: 'En Meta y Google', icono: 'travel_explore' },
    ],
    ejemploOtro: 'Ej: en TikTok',
    porQue: 'Lo tengo en cuenta al crear tus piezas.',
    icono: 'campaign',
  },
  {
    // Si es una plataforma, el onboarding muestra su paso de conexión
    // (ADR-2026-09-29-onboarding-v2-canal-de-venta).
    clave: 'canal',
    titulo: '¿Dónde vende tu marca hoy?',
    opciones: [
      { id: 'tiendanube', label: 'Tiendanube', icono: 'storefront' },
      { id: 'shopify', label: 'Shopify', icono: 'shopping_cart' },
      { id: 'mercadolibre', label: 'Mercado Libre', icono: 'local_shipping' },
      { id: 'ninguno', label: 'Ninguno', icono: 'do_not_disturb_on', exclusiva: true },
    ],
    ejemploOtro: 'Ej: por Instagram y WhatsApp',
    porQue: 'Si es una tienda online, después te ayudo a conectarla.',
    icono: 'storefront',
  },
];

export const LETRAS_DE_OPCION = 'ABCDE';
export const MAX_OTRO = 120;

/**
 * Marcar o desmarcar una opción (ADR-2026-10-02-onboarding-v2-preguntas-de-varias), en el
 * orden en que se marcan: la primera es la principal. Una exclusiva queda sola; marcar otra
 * la saca.
 */
export function alternarOpcion(marcadas: readonly string[], id: string, pregunta: Pregunta): string[] {
  if (marcadas.includes(id)) return marcadas.filter((x) => x !== id);
  const exclusiva = (x: string) => pregunta.opciones.some((o) => o.id === x && o.exclusiva);
  if (exclusiva(id)) return [id];
  return [...marcadas.filter((x) => !exclusiva(x)), id];
}

/** La primera pregunta sin responder, o -1 si están todas. */
export function preguntaPendiente(respuestas: RespuestasDeLaEspera): number {
  return PREGUNTAS.findIndex((q) => !respuestas[q.clave]);
}

// ─── Lo que dice Cumbre ────────────────────────────────────────────────────────

/**
 * El globo de cada pregunta: un saludo que ubica (cuántas van, qué está haciendo Atlas),
 * la pregunta y por qué se pregunta. Con link, la pregunta del canal arranca con lo que
 * sugiere el link.
 */
export function dialogoDePregunta(p: {
  indice: number;
  manual: boolean;
  sugerida: 'tiendanube' | 'shopify' | null;
}): Tramo[] {
  const q = PREGUNTAS[p.indice];
  const total = PREGUNTAS.length;
  const saludo =
    p.indice === 0
      ? p.manual
        ? `Antes de armar tu marca, te hago ${total} preguntas.`
        : `Mientras analizo tu web, te hago ${total} preguntas.`
      : p.indice === total - 1
        ? 'Última pregunta.'
        : `Vamos por la ${p.indice + 1} de ${total}.`;
  const porQue =
    q.clave === 'canal' && p.sugerida
      ? `Por tu link, diría que ${p.sugerida === 'tiendanube' ? 'Tiendanube' : 'Shopify'}. ${q.porQue}`
      : q.porQue;
  return [
    { texto: saludo, estilo: 'saludo' },
    { texto: q.titulo, estilo: 'pregunta' },
    { texto: porQue, estilo: 'detalle' },
  ];
}

/**
 * Lo que dice Cumbre apenas la persona responde, antes de pasar a la siguiente. `ids`: todo lo
 * que marcó, la principal primero.
 */
export function reaccionARespuesta(clave: ClaveDePregunta, ids: readonly string[], n: number): Reaccion {
  const id = ids[0] ?? '';
  const varias = ids.length > 1;
  const texto = (() => {
    if (varias) {
      switch (clave) {
        case 'primero':
          return '¡Excelente! Empiezo por la primera que marcaste.';
        case 'rol':
          return 'Un gusto. Ya sé con quién hablo.';
        case 'anunciosPagos':
          return 'Genial, lo tengo en cuenta.';
        case 'canal':
          return ids.some((c) => c === 'tiendanube' || c === 'shopify' || c === 'mercadolibre')
            ? 'Perfecto. Después conectamos tu tienda.'
            : 'Anotado, gracias.';
      }
    }
    if (id === 'otro') return 'Anotado, gracias.';
    switch (clave) {
      case 'primero':
        return '¡Excelente! Empezamos por ahí.';
      case 'rol':
        return 'Un gusto. Ya sé con quién hablo.';
      case 'anunciosPagos':
        return id === 'no' ? 'No hay problema, para eso también estoy.' : 'Genial, lo tengo en cuenta.';
      case 'canal':
        return id === 'ninguno' ? 'Anotado. Seguimos igual.' : 'Perfecto. Después la conectamos.';
    }
  })();
  return { id: `respuesta-${clave}-${n}`, texto };
}

/**
 * Cómo va la lectura de la web mientras se responde. `incompleta`: el análisis terminó con
 * error; el tablero dice qué falta, acá no se promete nada.
 */
export type EstadoDeLaLectura = 'leyendo' | 'leida' | 'incompleta' | 'sin-web';
